import { useEffect } from 'react'

export interface KeyboardActions {
  openPalette: () => void
  openHelp: () => void
  focusComposer: () => void
  moveSelection: (direction: 1 | -1) => void
  replyToSelected: () => void
  jumpToNewest: () => void
  escape: () => void
  selectRoom: (index: number) => void
}

/** The bindings from the prototype. */
export const SHORTCUTS: ReadonlyArray<{ keys: string; what: string }> = [
  { keys: '⌘K', what: 'Go to a room' },
  { keys: '⌘1–9', what: 'Switch room in this organisation' },
  { keys: 'j / k', what: 'Move down and up the room' },
  { keys: 'r / ⏎', what: 'Reply to the selected message' },
  { keys: '/', what: 'Jump to the composer' },
  { keys: '⇧G', what: 'Back to the newest message' },
  { keys: 'esc', what: 'Close, then deselect' },
  { keys: '?', what: 'This list' },
]

/**
 * Whether this key belongs to whatever has focus.
 *
 * Enter and Space activate a focused button, link or toggle. A global binding
 * that swallows them makes every control unreachable from the keyboard, which
 * is the opposite of what nine bindings are for. Narrow on purpose: `?` and
 * `j` are not keys a button owns, so they keep working wherever focus is.
 */
function belongsToFocus(event: KeyboardEvent, target: HTMLElement | null): boolean {
  if (!target) return false
  if (event.key !== 'Enter' && event.key !== ' ') return false
  const tag = target.tagName.toLowerCase()
  return tag === 'button' || tag === 'a' || tag === 'select' || tag === 'summary'
    || target.isContentEditable || target.getAttribute('role') === 'switch'
}

export function useKeyboard(actions: KeyboardActions) {
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      const target = event.target as HTMLElement | null
      const tag = (target?.tagName ?? '').toLowerCase()
      const typing = tag === 'input' || tag === 'textarea'
      const modifier = event.metaKey || event.ctrlKey

      if (modifier && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        actions.openPalette()
        return
      }
      if (modifier && /^[1-9]$/.test(event.key)) {
        event.preventDefault()
        actions.selectRoom(Number(event.key) - 1)
        return
      }
      if (event.key === 'Escape') {
        actions.escape()
        return
      }
      // Everything below is a bare key, so it must not fire while typing, and
      // must leave a focused control's own activation keys alone.
      if (typing || belongsToFocus(event, target)) return

      if (event.key === 'j') { event.preventDefault(); actions.moveSelection(1) }
      else if (event.key === 'k') { event.preventDefault(); actions.moveSelection(-1) }
      else if (event.key === 'r' || event.key === 'Enter') { event.preventDefault(); actions.replyToSelected() }
      else if (event.key === '/') { event.preventDefault(); actions.focusComposer() }
      else if (event.key === '?') { event.preventDefault(); actions.openHelp() }
      else if (event.key === 'G') { event.preventDefault(); actions.jumpToNewest() }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [actions])
}
